import React, { useEffect, useRef } from "react";
/**
 * Table.js scenarios reference (render order below varies by conditions):
 * 1) No subquestions, ALL locations → Display all states/locals table (Scenario 1 block).
 * 2) With subquestions, ALL locations → Display grouped states tables (Scenario 2 block).
 * 3) Specific location, subquestion list → Display subquestions for that location (Scenario 3 block).
 * 4) Patterns over time available → Display POT table (Scenario 4 block).
 * 5) No data message → Fallbacks for patterns/non-patterns (two blocks at bottom).
 *
 * The comparison selector for ALL locations (year-only) is rendered here, above the State/Local tables.
 * Header-level comparison (specific location flows) is handled in QuestionHeader.
 */
import SubQuestionTable from "./TableTypes/SubQuestionTable";
import ComparisonTable from "./TableTypes/ComparisonTable";
import ComparisonSelector from "./ComparisonSelector";
import DataVisualizationToggle from "./DataVisualizationToggle";
import SampleSizeToggle from "./SampleSizeToggle";
import { topicDictionary } from "../Util/tileDictionary";

const Table = ({
  comparisonData,
  fetchTableDataForLocation,
  isPatternsOverTime,
  showSampleSize,
  setShowSampleSize,
  onVizTypeChange,
  locationAbbr,
  location,
  locationSelectedFromDropdownMenu,
  onLocationChange,
  onSubQuestionChange,
  onCompareSecondLocation,
  locations,
  question,
  questionId,
  selectedData,
  scrollToTableHeader,
  setIsPatternsOverTime,
  setSubQuestionTableData,
  setSubQuestionCSVData,
  subQuestionId,
  subQuestionTableData,
  subQuestion,
  subQuestionValue,
  vizType,
  year,
}) => {
  const tableHeaderRef = useRef(null);
  const delayedDisplay = useRef("none");
  const isAllLocations = locationAbbr === "ALL";
  // Treat "no subquestions" as a valid selection so the sample-size
  // toggle stays visible for those questions. Otherwise, require an
  // actual subQuestion object before showing the toggle.
  const hasSubQuestionSelected =
    !!subQuestion || (question?.SubQuestions?.length === 0);
  const showComparison =
    vizType === "table" &&
    comparisonData &&
    comparisonData.Rows &&
    comparisonData.Rows.length > 0;

  useEffect(() => {
    document.title = `School Health Profiles Explorer | 
    ${location.LocationDesc + " Results"} 
    `;
  }, [location]);

  useEffect(() => {
    delayedDisplay.current = "none";

    setTimeout(() => (delayedDisplay.current = "block"), 1000);
  }, [location, isPatternsOverTime]);

  setTimeout(() => (delayedDisplay.current = "block"), 500);

  const allLocations = locationAbbr === "ALL" ? true : false;

  // NOTE: This test fails when location !== "ALL"
  if (
    locationAbbr === "ALL" &&
    subQuestionTableData !== null &&
    !Array.isArray(subQuestionTableData) &&
    subQuestionTableData.hasOwnProperty("tableData") &&
    subQuestionTableData.tableData.length > 0 &&
    location.LocationType !== "National"
  ) {
    subQuestionTableData.tableData[0].LocationType =
      locationAbbr === "ALL"
        ? "State"
        : subQuestionTableData.tableData[0].LocationType;

    subQuestionTableData.tableData[0].LocationAlias =
      locationAbbr === "ALL" && "National";
  }
  const locationTypes =
    subQuestionTableData &&
    subQuestionTableData.hasOwnProperty("tableData") &&
    subQuestionTableData.tableData.length > 0 &&
    location.LocationAbbr === "ALL"
      ? Array.from(
          new Set(subQuestionTableData.tableData.map((i) => i.LocationType))
        )
      : [location.LocationType];

  const tableTitles = ["State Survey", "Local Survey", "Territory Survey"];

  const getTableTitle = (locationArrayIndex) => {
    const title =
      location.LocationAbbr !== "ALL"
        ? `${location.LocationDesc} Survey Results`
        : tableTitles[locationArrayIndex];
    return title;
  };

  const hasTableData = (dataObj) => {
    if (dataObj && dataObj.hasOwnProperty("tableData")) {
      return dataObj.tableData.length > 0 ? true : false;
    }
    return true;
  };

  return (
    <>
      <div className="row" ref={tableHeaderRef}>
        <div className="col-12 question-header__location-text">
          {!isPatternsOverTime && vizType === "table" && subQuestion && (
            <h2
              className="mt-4  main-table-header"
              style={{
                color: topicDictionary[question.TopicId].color,
                fontSize: "30px",
              }}
            >
              {subQuestion.SubQuestion}
            </h2>
          )}
          {locationAbbr === "ALL" && (question.SubQuestions.length === 0 || subQuestion) && (
            <div className="mb-2 w-50 align-items-center mx-auto">
              <DataVisualizationToggle
                onVizTypeChange={onVizTypeChange}
                vizType={vizType}
                question={question}
                subQuestionValue={subQuestionValue}
                locationAbbr={locationAbbr}
                allowedOptions={["graph", "table", "map"]}
              />
            </div>
          )}

          {/* Comparison selector for All Locations - below main-table-header, above #State-table
              Show only in View #1 (All Locations + subquestion) when there are
              subquestions to choose from. */}
          {!isPatternsOverTime && vizType === "table" && isAllLocations && hasSubQuestionSelected && (
            <div className="mb-3 comparison-container question-header__year-comparison-controls question-header__year-comparison-controls--table qh-view-1">
              {question?.SubQuestions && question.SubQuestions.length > 0 && (
                <ComparisonSelector
                  locations={locations}
                  questionId={questionId}
                  subQuestionId={subQuestionId}
                  year={year}
                  selectedData={selectedData}
                  excludeLocationAbbr={locationAbbr}
                  mode="allLocations"
                  // Variant: comparison control shown on the All Locations (Nationwide) view
                  // Unique classname: .comparison-selector--all-locations
                  variant="all-locations"
                  subQuestionText={subQuestion ? subQuestion.SubQuestion : ""}
                  summaryText={(!question?.SubQuestions || question.SubQuestions.length === 0) ? (question ? question.SummaryText : "") : ""}
                  onCompare={(payload) => {
                    onCompareSecondLocation && onCompareSecondLocation(payload);
                  }}
                  showCancel={false}
                />
              )}

              {/* Show Sample Size toggle whenever we're in the All Locations table view
                  and have a valid context (either no subquestions or an active selection). */}
              <div className="mt-2 d-flex justify-content-end">
                <SampleSizeToggle
                  id="tableShowSampleSizeCheckbox"
                  checked={!!showSampleSize}
                  onChange={(next) => {
                    if (typeof setShowSampleSize === "function") {
                      setShowSampleSize(next);
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>



      {/* When comparing years: render comparison tables for ALL or View 2 */}
      {showComparison && isAllLocations && (
        <>
          {/* State surveys table */}
          <ComparisonTable
            data={{
              ...comparisonData,
              Rows: (comparisonData?.Rows || []).filter(r => r?.Location?.Type === "National" || r?.Location?.Type === "State"),
            }}
            question={question}
            showSampleSize={showSampleSize}
            title={"State Surveys"}
            variant="view1"
            onLocationChange={onLocationChange}
          />

          {/* Local surveys table (render only if there are local rows) */}
          {comparisonData?.Rows?.some(r => r?.Location?.Type === "Local") && (
            <ComparisonTable
              data={{
                ...comparisonData,
                Rows: (comparisonData?.Rows || []).filter(r => r?.Location?.Type === "Local"),
              }}
              question={question}
              showSampleSize={showSampleSize}
              title={"Local Surveys"}
              variant="view1"
              onLocationChange={onLocationChange}
            />
          )}
        </>
      )}

      {showComparison && !isAllLocations && (
        <ComparisonTable
          data={comparisonData}
          question={question}
          showSampleSize={showSampleSize}
          title={""}
          variant={(subQuestion || isPatternsOverTime) ? "view3" : "view2"}
          onSubQuestionChange={subQuestion ? undefined : onSubQuestionChange}
          questionId={questionId}
        />
      )}
      {
        /****************************************
         * Scenario 1: Display all states Table
         * for items with no subquestions
         ************************************/
        !showComparison &&
          question &&
          question.SubQuestions.length === 0 &&
          location.LocationAbbr === "ALL" &&
          subQuestionTableData &&
          subQuestionTableData.tableData &&
          locationTypes.map((type, index) => {
            const data = {
              tableData: subQuestionTableData.tableData.filter((i) => {
                return i.LocationType === type;
              }),
              medianRangeData: subQuestionTableData.medianRangeData,
            };
            const title = getTableTitle(index);
            return (
              <div id={`${type}-table`} key={`scenario1-${type}-${index}`}>
                <SubQuestionTable
                  key={`table-${index}`}
                  isScrollToTop={false}
                  parentLocation={location}
                  locationAbbr={locationAbbr}
                  showSampleSize={showSampleSize}
                  subQuestionId={subQuestionId}
                  questionId={questionId}
                  year={year}
                  title={"scenario 1"}
                  tableName={title}
                  question={question}
                  subQuestion={subQuestion}
                  subQuestionTableData={data}
                  allLocations={allLocations}
                  linkFirstColumn={true}
                  comparisonData={comparisonData}
                  onLocationChange={onLocationChange}
                  onSubQuestionChange={onSubQuestionChange}
                  fetchTableDataForLocation={fetchTableDataForLocation}
                  isPatternsOverTime={isPatternsOverTime}
                  setIsPatternsOverTime={setIsPatternsOverTime}
                />
              </div>
            );
          })
      }
      {
        /****************************************
         * Scenario 2: Display all states table
         * for items WITH sub-questions
         ************************************/
        !showComparison &&
          subQuestionTableData !== null &&
          question &&
          !isPatternsOverTime &&
          question.SubQuestions.length > 0 &&
          location.LocationAbbr === "ALL" &&
          subQuestionTableData.hasOwnProperty("tableData") &&
          locationTypes.map((type, index) => {
            const data = {
              tableData: subQuestionTableData.tableData.filter((i) => {
                return i.LocationType === type;
              }),
              medianRangeData: subQuestionTableData.medianRangeData,
            };
            const title = getTableTitle(index);
            return (
              <div id={`${type}-table`} key={`scenario2-${type}-${index}`}>
                <SubQuestionTable
                  allLocations={allLocations}
                  comparisonData={comparisonData}
                  fetchTableDataForLocation={fetchTableDataForLocation}
                  isPatternsOverTime={isPatternsOverTime}
                  showSampleSize={showSampleSize}
                  linkFirstColumn={true}
                  locationAbbr={locationAbbr}
                  onLocationChange={onLocationChange}
                  onSubQuestionChange={onSubQuestionChange}
                  parentLocation={location}
                  question={question}
                  questionId={questionId}
                  tableName={title}
                  tableHeaderRef={tableHeaderRef}
                  subQuestionId={subQuestionId}
                  subQuestion={subQuestion}
                  subQuestionTableData={data}
                  scrollToTableHeader={
                    type === "State" && scrollToTableHeader ? true : false
                  }
                  tableIndex={index}
                  title={"scenario 2"}
                  year={year}
                />
              </div>
            );
          })
      }
      {
        /****************************************
         * Scenario 3: Display all subquestions
         * associated with a specific location
         * Handles both array format and object format (with tableData property)
         ************************************/
        !showComparison &&
          location.LocationAbbr !== "ALL" &&
          subQuestionTableData &&
          !isPatternsOverTime &&
          (
            // Array format: subQuestionTableData is an array with items
            (Array.isArray(subQuestionTableData) && subQuestionTableData.length > 0) ||
            // Object format: subQuestionTableData has tableData array with items
            (subQuestionTableData.hasOwnProperty("tableData") && subQuestionTableData.tableData && subQuestionTableData.tableData.length > 0)
          ) && (
            <div id={`${location.LocationType}-table`} key={`scenario3-${location.LocationType}`}>
              <SubQuestionTable
                allLocations={allLocations}
                comparisonData={comparisonData}
                fetchTableDataForLocation={fetchTableDataForLocation}
                isScrollToTop={true}
                isPatternsOverTime={isPatternsOverTime}
                showSampleSize={showSampleSize}
                linkFirstColumn={true}
                locationAbbr={locationAbbr}
                onLocationChange={onLocationChange}
                onSubQuestionChange={onSubQuestionChange}
                parentLocation={location}
                questionId={questionId}
                question={question}
                scrollToTableHeader={true}
                subQuestionId={subQuestionId}
                subQuestion={subQuestion}
                subQuestionTableData={
                  // Normalize: if object format, pass the whole object; SubQuestionTable handles both
                  subQuestionTableData
                }
                tableName={``}
                tableHeaderRef={tableHeaderRef}
                title={"scenario 3"}
                year={year}
              />
            </div>
          )
      }
      {
        /******************************************
         * Scenario 4: Displays patterns over time
         * when data is available
         ******************************************/
        !showComparison &&
          isPatternsOverTime &&
          subQuestionTableData !== null &&
          hasTableData(subQuestionTableData) && (
            <div id={`${location.LocationType}-table`} key={`scenario4-${location.LocationType}`}>
              <SubQuestionTable
                isScrollToTop={false}
                parentLocation={location}
                locationAbbr={locationAbbr}
                showSampleSize={showSampleSize}
                subQuestionId={subQuestionId}
                questionId={questionId}
                year={year}
                linkFirstColumn={true}
                tableName={`${location.LocationDesc} Survey Results`}
                question={question}
                title={"scenario 3"}
                subQuestion={subQuestion}
                subQuestionTableData={subQuestionTableData}
                allLocations={allLocations}
                comparisonData={comparisonData}
                onLocationChange={onLocationChange}
                fetchTableDataForLocation={fetchTableDataForLocation}
                onSubQuestionChange={onSubQuestionChange}
                isPatternsOverTime={isPatternsOverTime}
              />
            </div>
          )
      }
      {
        /******************************************
         * Scenario 5: Displays "no data" message
         * when "isPatternsOverTime" === true
         ******************************************/
        isPatternsOverTime && !hasTableData(subQuestionTableData) && (
          <div style={{ height: 100 }}>No data for selected location.</div>
        )
      }
      {
        /******************************************
         * Scenario 6: Displays "no data" message
         * when "isPatternsOverTime" === false
         * and no table data is available for a specific location
         ******************************************/
        selectedData.location.LocationAbbr !== "ALL" &&
          subQuestionTableData === null &&
          !isPatternsOverTime && (
            <div style={{ height: 100 }}>No data for selected location.</div>
          )
      }
    </>
  );
};

export default Table;
